/* ================================================================
   MarketingPage.tsx — Landing Page My Helper MINESEC (V3)
   Hero : slider 3 slides | Section "Ils nous font confiance"
   Header + Footer : PublicLayout | Bilingue FR/EN
   Date: 2026-09-12
   ================================================================ */

import { useState, useEffect, useCallback, useMemo } from 'react'
import {
  GraduationCap, BookOpen, Users, Wallet,
  MessageSquare, BarChart3, ShieldCheck, CheckCircle, ArrowRight,
  Star, Send,
  Zap, Lock, Smartphone, Award, ChevronRight, Play,
  FileText, Shield, Newspaper, FileSearch, Calculator,
  Clock, Check, ChevronLeft, Quote,
} from 'lucide-react'
import { useLang } from '@/hooks/useLang'
import { toast } from 'sonner'
import PublicLayout from '@/components/layout/PublicLayout'

const API_BASE = (import.meta as any).env?.VITE_API_URL || '/api/v1'

/* ─────────────────────── 12 MODULES ─────────────────────── */

const MODULES = [
  { id: 1, icon: Users, title: 'Élèves', titleEn: 'Students',
    desc: 'Gestion complète des informations et du parcours des apprenants. Inscription, transferts, cas sociaux.',
    descEn: 'Complete management of learner information and academic journey. Enrollment, transfers, social cases.',
    color: 'bg-blue-500', hoverColor: 'group-hover:bg-blue-600', badge: 'Inscription', badgeEn: 'Enrollment' },
  { id: 2, icon: GraduationCap, title: 'Enseignants', titleEn: 'Teachers',
    desc: 'Gestion des enseignants, affectations, charges horaires et suivi pédagogique.',
    descEn: 'Teacher management, assignments, workload tracking and pedagogical follow-up.',
    color: 'bg-emerald-500', hoverColor: 'group-hover:bg-emerald-600', badge: 'Pédagogie', badgeEn: 'Pedagogy' },
  { id: 3, icon: BookOpen, title: 'Classes & Matières', titleEn: 'Classes & Subjects',
    desc: "Organisation des classes, matières, spécialités, niveaux et emplois du temps.",
    descEn: 'Organization of classes, subjects, specialties, levels and timetables.',
    color: 'bg-violet-500', hoverColor: 'group-hover:bg-violet-600', badge: 'Organisation', badgeEn: 'Organization' },
  { id: 4, icon: BarChart3, title: 'Notes & Bulletins', titleEn: 'Grades & Reports',
    desc: 'Saisie des notes, calcul automatique, génération des bulletins et relevés de notes.',
    descEn: 'Grade entry, automatic calculation, report card and transcript generation.',
    color: 'bg-amber-500', hoverColor: 'group-hover:bg-amber-600', badge: 'Évaluation', badgeEn: 'Assessment' },
  { id: 5, icon: CheckCircle, title: 'Assiduité', titleEn: 'Attendance',
    desc: 'Suivi des présences, absences, retards et sanctions disciplinaires en temps réel.',
    descEn: 'Real-time tracking of attendance, absences, lateness and disciplinary sanctions.',
    color: 'bg-rose-500', hoverColor: 'group-hover:bg-rose-600', badge: 'Discipline', badgeEn: 'Discipline' },
  { id: 6, icon: Wallet, title: 'Finances & Budget', titleEn: 'Finance & Budget',
    desc: 'Gestion des frais scolaires, paiements, suivi budgétaire et rapports financiers.',
    descEn: 'Tuition fee management, payments, budget tracking and financial reports.',
    color: 'bg-cyan-500', hoverColor: 'group-hover:bg-cyan-600', badge: 'Finances', badgeEn: 'Finance' },
  { id: 7, icon: MessageSquare, title: 'Messagerie', titleEn: 'Messaging',
    desc: 'Communication interne entre administration, enseignants, parents et élèves via SMS et email.',
    descEn: 'Internal communication between administration, teachers, parents and students via SMS and email.',
    color: 'bg-indigo-500', hoverColor: 'group-hover:bg-indigo-600', badge: 'Communication', badgeEn: 'Communication' },
  { id: 8, icon: FileText, title: 'Rapports & Statistiques', titleEn: 'Reports & Statistics',
    desc: 'Tableaux de bord, rapports MINESEC, taux de réussite, listes et procès-verbaux.',
    descEn: 'Dashboards, MINESEC reports, pass rates, lists and official transcripts.',
    color: 'bg-teal-500', hoverColor: 'group-hover:bg-teal-600', badge: 'Analyse', badgeEn: 'Analytics' },
  { id: 9, icon: Shield, title: 'Administration', titleEn: 'Administration',
    desc: 'Gestion des utilisateurs, rôles, permissions, paramètres système et sécurité.',
    descEn: 'User management, roles, permissions, system settings and security.',
    color: 'bg-slate-500', hoverColor: 'group-hover:bg-slate-600', badge: 'Admin', badgeEn: 'Admin' },
  { id: 10, icon: FileSearch, title: 'Audit & Logs', titleEn: 'Audit & Logs',
    desc: "Journal d'activités, traçabilité des actions, rapports de sécurité et conformité.",
    descEn: 'Activity journal, action traceability, security and compliance reports.',
    color: 'bg-orange-500', hoverColor: 'group-hover:bg-orange-600', badge: 'Sécurité', badgeEn: 'Security' },
  { id: 11, icon: Newspaper, title: 'Contenu & Actualités', titleEn: 'Content & News',
    desc: "Publication d'actualités, événements scolaires, forum et communication externe.",
    descEn: 'News publishing, school events, forum and external communication.',
    color: 'bg-pink-500', hoverColor: 'group-hover:bg-pink-600', badge: 'Contenu', badgeEn: 'Content' },
  { id: 12, icon: Calculator, title: 'Conseil de Classe', titleEn: 'Class Council',
    desc: "Délibérations, décisions fin d'année, conseils express et orientations.",
    descEn: 'Deliberations, year-end decisions, express councils and student orientation.',
    color: 'bg-lime-600', hoverColor: 'group-hover:bg-lime-700', badge: 'Académique', badgeEn: 'Academic' },
]

/* ───────────────── HERO SLIDES (3) ─────────────────
   Images : placez vos photos dans public/images/hero/slide1..3.jpg
   → elles s'affichent automatiquement ; sinon le dégradé
   professionnel + motif SVG prend le relais (aucune image cassée). */

const HERO_SLIDES = [
  { img: '/images/hero/slide1.jpg', gradient: 'from-blue-950 via-blue-900 to-blue-800' },
  { img: '/images/hero/slide2.jpg', gradient: 'from-indigo-950 via-blue-900 to-blue-800' },
  { img: '/images/hero/slide3.jpg', gradient: 'from-blue-900 via-blue-950 to-indigo-900' },
]

/* ───────────── ILS NOUS FONT CONFIANCE ───────────── */

const TRUSTED_SCHOOLS = [
  {
    initials: 'LB',
    name: 'Lycée Bilingue de Baleng',
    city: 'Bafoussam',
    color: 'bg-blue-600',
    quoteFr: 'Une gestion simplifiée de A à Z — les bulletins se génèrent en un clic.',
    quoteEn: 'Simplified management from A to Z — report cards generated in one click.',
  },
  {
    initials: 'GG',
    name: 'Collège Grace of God',
    city: 'Douala',
    color: 'bg-amber-500',
    quoteFr: 'Le suivi des élèves et des finances n\'a jamais été aussi clair.',
    quoteEn: 'Student and finance tracking has never been so clear.',
  },
]

/* ─────────────────────── COMPONENT ─────────────────────── */

export default function MarketingPage() {
  const { lang } = useLang()
  const isFr = lang === 'fr'

  // Slider state
  const [currentSlide, setCurrentSlide] = useState(0)
  const [paused, setPaused] = useState(false)

  // « Ils nous font confiance » : chargés depuis l'API admin, fallback codé
  const [partners, setPartners] = useState(TRUSTED_SCHOOLS)

  useEffect(() => {
    fetch(`${API_BASE}/content/public/partners`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (d?.items?.length) setPartners(d.items)
      })
      .catch(() => { /* fallback : TRUSTED_SCHOOLS */ })
  }, [])

  useEffect(() => {
    if (paused) return
    const t = setInterval(() => setCurrentSlide((c) => (c + 1) % HERO_SLIDES.length), 6000)
    return () => clearInterval(t)
  }, [paused])

  const goTo = useCallback((i: number) => setCurrentSlide((i + HERO_SLIDES.length) % HERO_SLIDES.length), [])

  // Formulaire démo
  const [demoForm, setDemoForm] = useState({
    full_name: '', email: '', school_name: '', phone: '', message: '',
  })
  const [demoSubmitting, setDemoSubmitting] = useState(false)

  const scrollTo = useCallback((id: string) => {
    const el = document.getElementById(id)
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }, [])

  const handleDemoSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!demoForm.full_name || !demoForm.email) {
      toast.error(isFr ? 'Veuillez remplir les champs obligatoires' : 'Please fill in the required fields')
      return
    }
    setDemoSubmitting(true)
    try {
      const res = await fetch(`${API_BASE}/demo-requests`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(demoForm),
      })
      if (res.ok) {
        toast.success(isFr ? 'Demande envoyée avec succès ! Nous vous contacterons bientôt.' : 'Request sent successfully! We will contact you soon.')
        setDemoForm({ full_name: '', email: '', school_name: '', phone: '', message: '' })
      } else {
        const err = await res.json().catch(() => ({}))
        toast.error(err.detail || (isFr ? "Erreur lors de l'envoi" : 'Error sending request'))
      }
    } catch {
      toast.error(isFr ? 'Erreur réseau' : 'Network error')
    } finally {
      setDemoSubmitting(false)
    }
  }

  const t = useMemo(() => ({
    hero: {
      badge: isFr ? '✦ Conforme MINESEC' : '✦ MINESEC Compliant',
      slides: [
        {
          kicker: isFr ? 'Plateforme de gestion scolaire' : 'School management platform',
          title1: isFr ? 'GÉREZ.' : 'MANAGE.',
          title2: isFr ? 'SIMPLIFIEZ.' : 'SIMPLIFY.',
          title3: isFr ? 'RÉUSSISSEZ.' : 'SUCCEED.',
          subtitle: isFr
            ? "La solution digitale tout-en-un pour la gestion efficace, sécurisée et conforme aux exigences du MINESEC. De l'inscription aux bulletins, tout en un seul clic."
            : 'The all-in-one digital solution for efficient, secure school management compliant with MINESEC requirements. From enrollment to report cards, all in one click.',
        },
        {
          kicker: isFr ? '12 modules interconnectés' : '12 interconnected modules',
          title1: isFr ? 'TOUT.' : 'EVERYTHING.',
          title2: isFr ? 'EN UN.' : 'IN ONE.',
          title3: isFr ? 'SEUL ENDROIT.' : 'SINGLE PLACE.',
          subtitle: isFr
            ? 'Élèves, enseignants, notes, emplois du temps, finances, discipline et rapports MINESEC — centralisés dans une plateforme unique et moderne.'
            : 'Students, teachers, grades, timetables, finance, discipline and MINESEC reports — centralized in a single modern platform.',
        },
        {
          kicker: isFr ? 'Sécurité & fiabilité' : 'Security & reliability',
          title1: isFr ? 'VOS DONNÉES.' : 'YOUR DATA.',
          title2: isFr ? 'PROTÉGÉES.' : 'PROTECTED.',
          title3: isFr ? 'TOUJOURS.' : 'ALWAYS.',
          subtitle: isFr
            ? 'Sauvegardes automatiques, permissions fines par rôle et disponibilité 99,9%. Votre établissement travaille sereinement, nous veillons sur le reste.'
            : 'Automatic backups, fine-grained role permissions and 99.9% uptime. Your school works peacefully, we take care of the rest.',
        },
      ],
      ctaPrimary: isFr ? 'Demander une démo' : 'Request a demo',
      ctaSecondary: isFr ? 'Voir les 12 modules' : 'See all 12 modules',
    },
    trust: {
      title: isFr ? 'Pourquoi nous choisir ?' : 'Why choose us?',
      items: [
        { icon: Lock, title: isFr ? 'Sécurisé' : 'Secure', desc: isFr ? 'Données protégées et sauvegardées automatiquement' : 'Protected and automatically backed-up data' },
        { icon: Smartphone, title: isFr ? 'Accessible' : 'Accessible', desc: isFr ? 'Partout, sur tous vos appareils, 24h/24' : 'Everywhere, on all your devices, 24/7' },
        { icon: Zap, title: isFr ? 'Efficace' : 'Efficient', desc: isFr ? 'Gagnez du temps, améliorez le suivi pédagogique' : 'Save time, improve pedagogical tracking' },
        { icon: Award, title: 'Conforme MINESEC', desc: isFr ? 'Selon les normes officielles du Ministère' : 'According to official Ministry standards' },
      ],
    },
    trusted: {
      title: isFr ? 'Ils nous font confiance' : 'They trust us',
      subtitle: isFr
        ? 'Des établissements camerounais nous confient déjà leur gestion quotidienne'
        : 'Cameroonian schools already trust us with their daily management',
      since: isFr ? 'Client depuis' : 'Client since',
    },
    modules: {
      title: isFr ? '12 Modules Puissants' : '12 Powerful Modules',
      subtitle: isFr ? 'Une solution complète couvrant tous les aspects de la gestion scolaire secondaire' : 'A complete solution covering all aspects of secondary school management',
      cta: isFr ? 'En savoir plus' : 'Learn more',
    },
    demo: {
      title: isFr ? 'Demander une démonstration' : 'Request a demonstration',
      subtitle: isFr ? 'Remplissez le formulaire ci-dessous et notre équipe vous contactera sous 24h.' : 'Fill out the form below and our team will contact you within 24 hours.',
      form: {
        name: isFr ? 'Nom complet *' : 'Full name *',
        email: isFr ? 'Email *' : 'Email *',
        school: isFr ? "Nom de l'établissement" : 'School name',
        phone: isFr ? 'Téléphone' : 'Phone',
        message: isFr ? 'Message / Besoins spécifiques' : 'Message / Specific needs',
        submit: isFr ? 'Envoyer ma demande' : 'Send my request',
        sending: isFr ? 'Envoi en cours...' : 'Sending...',
      },
      features: isFr
        ? ['Réponse sous 24h', 'Démo personnalisée', 'Sans engagement', 'Accompagnement inclus']
        : ['Reply within 24h', 'Personalized demo', 'No commitment', 'Included onboarding'],
    },
  }), [isFr])

  return (
    <PublicLayout>
      {/* ═══════════════════ HERO SLIDER (3 slides) ═══════════════════ */}
      <section
        className="relative bg-blue-900 overflow-hidden"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
      >
        {/* Slides : remplissent l'écran sous le header fixe */}
        <div className="relative min-h-[560px] lg:min-h-[620px]">
          {HERO_SLIDES.map((slide, i) => (
            <div
              key={i}
              className={`absolute inset-0 transition-opacity duration-1000 ${
                i === currentSlide ? 'opacity-100' : 'opacity-0 pointer-events-none'
              }`}
            >
              {/* Image de fond professionnelle (fallback = dégradé + motif) */}
              <img
                src={slide.img}
                alt=""
                className="absolute inset-0 w-full h-full object-cover"
                onError={(e) => { e.currentTarget.style.display = 'none' }}
              />
              <div className={`absolute inset-0 bg-gradient-to-br ${slide.gradient} opacity-90`} />
              <div
                className="absolute inset-0 opacity-[0.07] pointer-events-none"
                style={{
                  backgroundImage:
                    'radial-gradient(circle at 25% 35%, white 1.2px, transparent 1.2px)',
                  backgroundSize: '34px 34px',
                }}
              />
              <div className="absolute bottom-0 right-0 w-[420px] h-[420px] bg-amber-400/10 rounded-full blur-3xl translate-x-1/4 translate-y-1/4 pointer-events-none" />

              {/* Contenu du slide */}
              <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-36 pb-28 lg:pt-44 lg:pb-32">
                <div className="max-w-3xl">
                  <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-400/10 border border-amber-400/30 rounded-full mb-6">
                    <Star className="w-4 h-4 text-amber-400 fill-amber-400" />
                    <span className="text-amber-400 text-sm font-semibold">
                      {i === 0 ? t.hero.badge : t.hero.slides[i].kicker}
                    </span>
                  </div>

                  <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black text-white leading-[0.95] tracking-tight mb-6">
                    <span className="block animate-none">{t.hero.slides[i].title1}</span>
                    <span className="block text-amber-400">{t.hero.slides[i].title2}</span>
                    <span className="block">{t.hero.slides[i].title3}</span>
                  </h1>

                  <p className="text-lg text-blue-100/80 max-w-xl mb-9 leading-relaxed">
                    {t.hero.slides[i].subtitle}
                  </p>

                  <div className="flex flex-wrap gap-4">
                    <button
                      type="button"
                      onClick={() => scrollTo('demo')}
                      className="group px-8 py-4 bg-amber-400 hover:bg-amber-300 text-blue-900 font-bold rounded-xl shadow-xl shadow-amber-400/20 transition-all hover:scale-105 flex items-center gap-2"
                    >
                      {t.hero.ctaPrimary}
                      <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                    </button>
                    <button
                      type="button"
                      onClick={() => scrollTo('modules')}
                      className="px-8 py-4 bg-white/10 hover:bg-white/20 text-white font-semibold rounded-xl border border-white/20 transition-all flex items-center gap-2"
                    >
                      <Play className="w-4 h-4" />
                      {t.hero.ctaSecondary}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Flèches navigation */}
        <button
          type="button"
          onClick={() => goTo(currentSlide - 1)}
          aria-label="Slide précédent"
          className="absolute left-4 top-1/2 -translate-y-1/2 z-10 p-3 rounded-full bg-white/10 hover:bg-white/25 border border-white/20 text-white backdrop-blur-sm transition-all"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        <button
          type="button"
          onClick={() => goTo(currentSlide + 1)}
          aria-label="Slide suivant"
          className="absolute right-4 top-1/2 -translate-y-1/2 z-10 p-3 rounded-full bg-white/10 hover:bg-white/25 border border-white/20 text-white backdrop-blur-sm transition-all"
        >
          <ChevronRight className="w-5 h-5" />
        </button>

        {/* Points de navigation */}
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 flex items-center gap-2.5">
          {HERO_SLIDES.map((_, i) => (
            <button
              key={i}
              type="button"
              onClick={() => goTo(i)}
              aria-label={`Slide ${i + 1}`}
              className={`h-2 rounded-full transition-all duration-500 ${
                i === currentSlide ? 'w-8 bg-amber-400' : 'w-2 bg-white/40 hover:bg-white/70'
              }`}
            />
          ))}
        </div>

        {/* Vague de transition */}
        <div className="absolute bottom-0 left-0 right-0 pointer-events-none z-[5]">
          <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full">
            <path d="M0 120L60 110C120 100 240 80 360 70C480 60 600 60 720 65C840 70 960 80 1080 85C1200 90 1320 90 1380 90L1440 90V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z" fill="white"/>
          </svg>
        </div>
      </section>

      {/* ═══════════════════ TRUST ═══════════════════ */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-black text-blue-900 mb-3">{t.trust.title}</h2>
            <div className="w-16 h-1 bg-amber-400 mx-auto rounded-full" />
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {t.trust.items.map((item, i) => (
              <div key={i} className="group p-6 rounded-2xl bg-gray-50 hover:bg-blue-900 transition-all duration-300 border border-gray-100 hover:border-blue-800">
                <div className="w-12 h-12 bg-blue-100 group-hover:bg-amber-400 rounded-xl flex items-center justify-center mb-4 transition-colors">
                  <item.icon className="w-6 h-6 text-blue-900" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 group-hover:text-white mb-2 transition-colors">{item.title}</h3>
                <p className="text-sm text-gray-500 group-hover:text-blue-200 transition-colors">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════ ILS NOUS FONT CONFIANCE ═══════════════════ */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-1.5 bg-amber-100 text-amber-700 text-xs font-bold uppercase tracking-wider rounded-full mb-4">
              {isFr ? 'Références' : 'References'}
            </span>
            <h2 className="text-3xl lg:text-4xl font-black text-blue-900 mb-3">{t.trusted.title}</h2>
            <p className="text-gray-500 max-w-xl mx-auto">{t.trusted.subtitle}</p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {partners.map((school) => (
              <div
                key={school.name}
                className="group bg-white rounded-2xl p-8 border border-gray-100 shadow-sm hover:shadow-xl hover:-translate-y-1 hover:border-amber-400/40 transition-all duration-300 relative overflow-hidden"
              >
                <Quote className="absolute top-6 right-6 w-8 h-8 text-gray-100 group-hover:text-amber-100 transition-colors" />
                <div className="flex items-center gap-4 mb-5">
                  <div className={`w-14 h-14 ${school.color} rounded-2xl flex items-center justify-center shadow-lg shrink-0`}>
                    <span className="text-white font-black text-xl">{school.initials}</span>
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 text-lg leading-tight">{school.name}</h3>
                    <p className="text-sm text-gray-500 flex items-center gap-1.5 mt-0.5">
                      <svg className="w-3.5 h-3.5 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                          d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      {school.city}
                    </p>
                  </div>
                </div>
                <p className="text-gray-600 leading-relaxed italic mb-4">
                  “{isFr ? school.quoteFr : school.quoteEn}”
                </p>
                <div className="flex items-center gap-1.5 text-amber-500">
                  {[...Array(5)].map((_, s) => (
                    <Star key={s} className="w-4 h-4 fill-amber-400 text-amber-400" />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════ 12 MODULES ═══════════════════ */}
      <section id="modules" className="py-20 bg-white scroll-mt-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <span className="inline-block px-4 py-1.5 bg-blue-100 text-blue-900 text-xs font-bold uppercase tracking-wider rounded-full mb-4">
              {isFr ? 'Fonctionnalités' : 'Features'}
            </span>
            <h2 className="text-3xl sm:text-4xl font-black text-blue-900 mb-4">{t.modules.title}</h2>
            <p className="text-gray-500 max-w-2xl mx-auto">{t.modules.subtitle}</p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {MODULES.map((mod) => (
              <div key={mod.id} className="group relative bg-gray-50 rounded-2xl p-6 hover:bg-white hover:shadow-xl transition-all duration-300 border border-gray-100 hover:border-amber-400/30 overflow-hidden">
                <div className={`absolute top-0 right-0 w-24 h-24 ${mod.color} opacity-5 rounded-bl-full transition-all ${mod.hoverColor}`} />
                <div className="flex items-center gap-3 mb-4">
                  <div className={`w-10 h-10 ${mod.color} rounded-lg flex items-center justify-center shadow-lg transition-transform group-hover:scale-110`}>
                    <mod.icon className="w-5 h-5 text-white" />
                  </div>
                  <span className="text-2xl font-black text-gray-200">{mod.id < 10 ? `0${mod.id}` : mod.id}</span>
                </div>
                <h3 className="text-lg font-bold text-gray-900 group-hover:text-blue-900 transition-colors mb-2">{isFr ? mod.title : mod.titleEn}</h3>
                <span className={`inline-block px-2 py-0.5 ${mod.color} bg-opacity-10 text-xs font-semibold rounded-md mb-3 ${mod.color.replace('bg-', 'text-')}`}>
                  {isFr ? mod.badge : mod.badgeEn}
                </span>
                <p className="text-sm text-gray-500 leading-relaxed">{isFr ? mod.desc : mod.descEn}</p>
                <div className="mt-4 flex items-center gap-1 text-amber-500 text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                  <span>{t.modules.cta}</span>
                  <ChevronRight className="w-4 h-4" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════ DEMO FORMULAIRE ═══════════════════ */}
      <section id="demo" className="py-20 bg-blue-900 relative overflow-hidden scroll-mt-24">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-amber-400/10 rounded-full blur-3xl" />
        </div>
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="text-white">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 border border-white/20 rounded-full mb-6">
                <Send className="w-4 h-4 text-amber-400" />
                <span className="text-amber-400 text-sm font-semibold">{isFr ? 'Démo gratuite' : 'Free demo'}</span>
              </div>
              <h2 className="text-3xl sm:text-4xl font-black mb-4">{t.demo.title}</h2>
              <p className="text-blue-200 mb-8">{t.demo.subtitle}</p>
              <div className="space-y-4">
                {t.demo.features.map((feat, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className="w-6 h-6 bg-amber-400 rounded-full flex items-center justify-center">
                      <Check className="w-4 h-4 text-blue-900" />
                    </div>
                    <span className="text-white font-medium">{feat}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-2xl">
              <form onSubmit={handleDemoSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t.demo.form.name}</label>
                  <input required type="text" value={demoForm.full_name}
                    onChange={(e) => setDemoForm({ ...demoForm, full_name: e.target.value })}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder={isFr ? 'Jean Dupont' : 'John Doe'} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t.demo.form.email}</label>
                  <input required type="email" value={demoForm.email}
                    onChange={(e) => setDemoForm({ ...demoForm, email: e.target.value })}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="email@exemple.com" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t.demo.form.school}</label>
                  <input type="text" value={demoForm.school_name}
                    onChange={(e) => setDemoForm({ ...demoForm, school_name: e.target.value })}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder={isFr ? 'Lycée de ...' : 'School name'} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t.demo.form.phone}</label>
                  <input type="tel" value={demoForm.phone}
                    onChange={(e) => setDemoForm({ ...demoForm, phone: e.target.value })}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="+237 ..." />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t.demo.form.message}</label>
                  <textarea rows={3} value={demoForm.message}
                    onChange={(e) => setDemoForm({ ...demoForm, message: e.target.value })}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                    placeholder={isFr ? 'Décrivez vos besoins...' : 'Describe your needs...'} />
                </div>
                <button type="submit" disabled={demoSubmitting}
                  className="w-full py-3 bg-amber-400 hover:bg-amber-300 disabled:bg-amber-200 text-blue-900 font-bold rounded-xl shadow-lg shadow-amber-400/20 transition-all hover:scale-[1.02] flex items-center justify-center gap-2">
                  {demoSubmitting ? (
                    <><Clock className="w-4 h-4 animate-spin" />{t.demo.form.sending}</>
                  ) : (
                    <><Send className="w-4 h-4" />{t.demo.form.submit}</>
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>
    </PublicLayout>
  )
}